
import CustomButton from "../CustomBlocks/Button";
import SlotBooking from "../CustomBlocks/SlotBooking";
import PaymentSection from "../CustomBlocks/PaymentSection";
import KycButton from "../CustomBlocks/KycButton";
import ESignButton from "../CustomBlocks/ESignButton";
import PhoneInput from "../CustomBlocks/PhoneInput";
import EmailInput from "../CustomBlocks/EmailInput";
import NameInput from "../CustomBlocks/NameInput";
import NumberInput from "../CustomBlocks/NumberInput";
import AddressInput from "../CustomBlocks/AddressInput";
import {
  SingleLineTextInput,
  MultiLineTextInput,
} from "../CustomBlocks/TextFieldInput";
import MultiSelectInput from "../CustomBlocks/MultiSelectInput";
import ChipSelectInput from "../CustomBlocks/ChipSelectInput";
import { RadioGroupInput } from "../CustomBlocks/ChoiceGroupInput";
import AiGenerator from "../CustomBlocks/AiGenerator";
import CrmAutocompleteInput from "../CustomBlocks/CrmAutocompleteInput";
import WorkflowPreviewBlock from "../CustomBlocks/WorkflowPreviewBlock";
import LoadingAnalysis from "../CustomBlocks/quiz/LoadingAnalysis";
import ResultSummary from "../CustomBlocks/quiz/ResultSummary";
import ProgressChart from "../CustomBlocks/quiz/ProgressChart";
import { SCALE_BLOCK_COMPONENTS } from "../CustomBlocks/scale";
import {
  DateInput,
  TimeInput,
  DateTimeLocalInput,
  MonthYearInput,
} from "../CustomBlocks/DateTimeInput";

// `group_name` drives the toolbox sections. react-form-builder2 collects items
// sharing a group_name under one heading; the toolbox CSS keeps every section
// expanded so they read as plain headings rather than accordions.
const GRID = 'Grid';
const BASIC_INFO = 'Basic Info';
const DATE_TIME = 'Date & Time';
const TEXTBOX = 'Textbox';
const CHOICES = 'Choices';
const MEDIA = 'Media';
const ACTIONS = 'Actions';
const QUIZ = 'Quiz & Results';
const SCALES = 'Rating & Scales';

/* Display-only quiz screens: `static` keeps them out of field-name checks and
   submission, `bare` skips the library's label wrapper. Settings live in
   `blockConfig`, seeded on drop by newFieldDefaults. */
const quizBlock = (key: string, name: string, marker: string, component: any) => ({
  key,
  element: 'CustomElement',
  component,
  type: 'custom',
  field_name: '',
  name,
  icon: `ms-icon ${marker}`,
  static: true,
  bare: true,
  group_name: QUIZ,
});

/* Answerable scale questions; settings live in `blockConfig`, seeded on drop by newFieldDefaults. */
const scaleBlock = (key: string, name: string, marker: string) => ({
  key,
  element: 'CustomElement',
  component: SCALE_BLOCK_COMPONENTS[key],
  type: 'custom',
  field_name: '',
  name,
  label: name,
  icon: `ms-icon ${marker}`,
  forwardRef: true,
  group_name: SCALES,
});


export const items = [
  {
    key: 'ColumnRow',
    element: 'MultiColumnRow',
    col_count: 1,
    class_name: 'col-12 ',
    field_name: 'col_row',
    name: 'Column',
    icon: 'ms-icon ms-column',
    group_name: GRID,
  },
  {
    key: 'TwoColumnRow',
    name: 'Two Column',
    canHaveDisplayHorizontal: true,
    icon: 'ms-icon ms-two-column',
    group_name: GRID,
  },
  {
    key: 'ThreeColumnRow',
    class_name: 'border-0',
    name: 'Three Column',
    canHaveDisplayHorizontal: true,
    icon: 'ms-icon ms-three-column',
    group_name: GRID,
  },
  {
    key: 'LineBreak',
    field_name: '',
    name: 'Line Break',
    icon: 'ms-icon ms-line-break',
    group_name: GRID,
  },
  {
    key: 'EmailInput',
    element: 'CustomElement',
    component: EmailInput,
    type: 'custom',
    // Blank for the same reason as NameInput below.
    field_name: '',
    name: 'Email',
    icon: 'ms-icon ms-email',
    forwardRef: true,
    label: 'Email',
    placeholder: 'you@example.com',
    group_name: BASIC_INFO,
  },
  {
    key: 'NameInput',
    element: 'CustomElement',
    component: NameInput,
    type: 'custom',
    // Intentionally blank: a non-empty field_name makes the library glue a raw
    // UUID onto it (toolbar.js `item.field_name + UUID()`). FormBuilder fills
    // in the clean default right after the field lands.
    field_name: '',
    name: 'Name',
    icon: 'ms-icon ms-name',
    forwardRef: true,
    label: 'Name',
    placeholder: 'Enter your full name',
    group_name: BASIC_INFO,
  },
  {
    key: 'NumberInput',
    element: 'CustomElement',
    component: NumberInput,
    type: 'custom',
    // Blank for the same reason as NameInput above.
    field_name: '',
    name: 'Number',
    icon: 'ms-icon ms-number',
    forwardRef: true,
    label: 'Number',
    placeholder: 'Enter a number',
    group_name: BASIC_INFO,
  },
  {
    key: 'AddressInput',
    element: 'CustomElement',
    component: AddressInput,
    type: 'custom',
    // Blank for the same reason as NameInput above.
    field_name: '',
    name: 'Address',
    icon: 'ms-icon ms-address',
    forwardRef: true,
    label: 'Address',
    group_name: BASIC_INFO,
  },
  {
    key: 'PhoneInput',
    element: 'CustomElement',
    component: PhoneInput,
    type: 'custom',
    // Blank for the same reason as NameInput above.
    field_name: '',
    name: 'Phone Number',
    icon: 'ms-icon ms-phone',
    forwardRef: true,
    label: 'Phone Number',
    defaultCountry: 'IN',
    group_name: BASIC_INFO,
  },
  {
    key: 'DateInput',
    element: 'CustomElement',
    component: DateInput,
    type: 'custom',
    field_name: '',
    name: 'Date',
    icon: 'ms-icon ms-date',
    forwardRef: true,
    label: 'Date',
    group_name: DATE_TIME,
  },
  {
    key: 'TimeInput',
    element: 'CustomElement',
    component: TimeInput,
    type: 'custom',
    field_name: '',
    name: 'Time',
    icon: 'ms-icon ms-time',
    forwardRef: true,
    label: 'Time',
    group_name: DATE_TIME,
  },
  {
    key: 'DateTimeInput',
    element: 'CustomElement',
    component: DateTimeLocalInput,
    type: 'custom',
    field_name: '',
    name: 'Date-Time',
    icon: 'ms-icon ms-datetime',
    forwardRef: true,
    label: 'Date & Time',
    group_name: DATE_TIME,
  },
  {
    key: 'MonthYearInput',
    element: 'CustomElement',
    component: MonthYearInput,
    type: 'custom',
    field_name: '',
    name: 'Month-Year',
    icon: 'ms-icon ms-month',
    forwardRef: true,
    label: 'Month & Year',
    group_name: DATE_TIME,
  },
  {
    key: 'SlotBooking',
    element: 'CustomElement',
    component: SlotBooking,
    type: 'custom',
    field_name: '',
    name: 'Slot Booking',
    icon: 'ms-icon ms-slot-booking',
    forwardRef: true,
    label: 'Slot Booking',
    group_name: DATE_TIME,
  },
  {
    key: 'HyperLink',
    field_name: '',
    name: 'Link',
    icon: 'ms-icon ms-link',
    group_name: BASIC_INFO,
  },
  /* Custom blocks rather than the library's own TextInput/TextArea: those build
     their input from a fixed prop list with no `placeholder` on it. `label` is
     set explicitly because buildItems() merges these onto react-form-builder2's
     defaults, whose label is the intl string "Placeholder Label" — leaving it
     out puts that phrase in every new field's Display Label. */
  {
    key: 'TextInput',
    element: 'CustomElement',
    component: SingleLineTextInput,
    type: 'custom',
    // Blank for the same reason as NameInput above.
    field_name: '',
    name: 'Text Input',
    label: 'Text Input',
    icon: 'ms-icon ms-text-input',
    forwardRef: true,
    placeholder: 'Enter text',
    group_name: TEXTBOX,
  },
  {
    key: 'TextArea',
    element: 'CustomElement',
    component: MultiLineTextInput,
    type: 'custom',
    field_name: '',
    name: 'Text Area',
    label: 'Text Area',
    icon: 'ms-icon ms-textarea',
    forwardRef: true,
    placeholder: 'Enter your message',
    group_name: TEXTBOX,
  },
  {
    key: 'Header',
    field_name: '',
    name: 'Header Text',
    icon: 'ms-icon ms-header',
    group_name: TEXTBOX,
  },
  {
    key: 'Paragraph',
    field_name: '',
    name: 'Paragraph',
    icon: 'ms-icon ms-paragraph',
    group_name: TEXTBOX,
  },
  /* A custom block rather than the library's own Tags: that one is built on
     react-select, whose div-based control no theme rule can reach, and which
     leaves a stray search box beside the sample chips on the canvas. */
  {
    key: 'Tags',
    element: 'CustomElement',
    component: MultiSelectInput,
    type: 'custom',
    field_name: '',
    name: 'Multi Select',
    label: 'Multi Select',
    icon: 'ms-icon ms-tags',
    forwardRef: true,
    canHaveOptionValue: true,
    options: [
      { value: 'option_1', text: 'Option 1' },
      { value: 'option_2', text: 'Option 2' },
      { value: 'option_3', text: 'Option 3' },
    ],
    group_name: TEXTBOX,
  },

  {
    key: 'ChipSelect',
    element: 'CustomElement',
    component: ChipSelectInput,
    type: 'custom',
    field_name: '',
    name: 'Chip Select',
    label: 'Chip Select',
    icon: 'ms-icon ms-chip-select',
    forwardRef: true,
    multiple: false,
    allowCustomValue: false,
    options: [
      { value: '1', text: 'Option 1', html: '<p>Option 1</p>' },
      { value: '2', text: 'Option 2', html: '<p>Option 2</p>' },
      { value: '3', text: 'Option 3', html: '<p>Option 3</p>' },
    ],
    group_name: TEXTBOX,
  },

  {
    key: 'CrmAutocomplete',
    element: 'CustomElement',
    component: CrmAutocompleteInput,
    type: 'custom',
    field_name: '',
    name: 'CRM Select',
    label: 'CRM Select',
    icon: 'ms-icon ms-crm-autocomplete',
    forwardRef: true,
    placeholder: '',
    crmSource: 'segment',
    crmFilters: {},
    group_name: CHOICES,
  },

  {
    key: 'RadioButtons',
    element: 'RadioButtons',
    component: RadioGroupInput,
    custom: true,
    type: 'custom',
    field_name: '',
    name: 'Radio',
    label: 'Radio',
    icon: 'ms-icon ms-radio',
    group_name: CHOICES,
  },
  {
    key: 'Checkboxes',
    field_name: '',
    name: 'Checkbox',
    label: 'Checkbox',
    icon: 'ms-icon ms-checkbox',
    group_name: CHOICES,
  },
  scaleBlock('RatingInput', 'Rating', 'ms-rating'),
  scaleBlock('OpinionScale', 'Opinion Scale', 'ms-opinion-scale'),
  scaleBlock('SliderInput', 'Slider', 'ms-slider'),
  {
    key: 'Image',
    field_name: '',
    name: 'Image',
    icon: 'ms-icon ms-image',
    group_name: MEDIA,
  },
  {
    key: 'CustomButton',
    element: 'CustomElement',
    component: CustomButton,
    type: 'custom',
    field_name: '',
    name: 'Button',
    icon: 'ms-icon ms-button',
    forwardRef: true,
    content: 'Submit',
    className: 'btn d-flex ',
    group_name: ACTIONS,
  },
  {
    key: 'AiGenerator',
    element: 'CustomElement',
    component: AiGenerator,
    type: 'custom',
    field_name: '',
    name: 'AI Generator',
    icon: 'ms-icon ms-ai-generator',
    forwardRef: true,
    label: 'AI Generator',
    buttonText: 'Generate',
    placeholder: 'Describe what you want to generate...',
    group_name: ACTIONS,
  },
  {
    key: 'WorkflowPreview',
    element: 'CustomElement',
    component: WorkflowPreviewBlock,
    type: 'custom',
    field_name: '',
    name: 'Workflow Preview',
    icon: 'ms-icon ms-workflow-preview',
    forwardRef: true,
    buttonText: 'Preview',
    group_name: ACTIONS,
  },
  quizBlock('LoadingAnalysis', 'Loading Analysis', 'ms-loading-analysis', LoadingAnalysis),
  quizBlock('ResultSummary', 'Result Summary', 'ms-result-summary', ResultSummary),
  quizBlock('ProgressChart', 'Progress Chart', 'ms-progress-chart', ProgressChart),
  {
    key: 'PaymentSection',
    element: 'CustomElement',
    component: PaymentSection,
    type: 'custom',
    field_name: '',
    name: 'Payment Section',
    icon: 'ms-icon ms-payment',
    forwardRef: true,
    content: 'Pay Now',
    className: 'btn d-flex ',
    group_name: ACTIONS,
  },
  {
    key: 'KycButton',
    element: 'CustomElement',
    component: KycButton,
    type: 'custom',
    field_name: '',
    name: 'KYC Button',
    icon: 'ms-icon ms-kyc',
    forwardRef: true,
    content: 'Verify KYC',
    className: 'btn d-flex ',
    group_name: ACTIONS,
  },
  {
    key: 'ESignButton',
    element: 'CustomElement',
    component: ESignButton,
    type: 'custom',
    field_name: '',
    name: 'eSign Button',
    icon: 'ms-icon ms-esign',
    forwardRef: true,
    content: 'Sign Document',
    className: 'btn d-flex ',
    group_name: ACTIONS,
  },
  // {
  //   key: 'FourColumnRow',
  //   element: 'MultiColumnRow',
  //   name: 'Four Column',
  //   canHaveDisplayHorizontal: true,

  // },
  // {
  //   key: 'FiveColumnRow',
  //   element: 'MultiColumnRow',
  //   name: 'Five Column',
  //   canHaveDisplayHorizontal: true,

  // },
  // {
  //   key: 'SixColumnRow',
  //   element: 'MultiColumnRow',
  //   canHaveDisplayHorizontal: true,
  //   name: 'Six Column',
  // },
];